import clsx from "clsx";
import { Loader2, TrendingUp } from "lucide-react";
import { useState } from "react";
import { Card } from "../ui";
import { monthTotal, SEGS, TIERS, type Money, type Seg } from "./money";
import { eur, eurShort, fmtDay, fmtYM, fmtYMLong, Kpi, Pill, SEG_BG, SortTh, type Inflows, type ProjectRow } from "./shared";

// How each item reads in the month list: paid vs the three kinds of pending
const ITEM_STATUS: Record<Seg, { label: string; tone: string }> = {
  paid: { label: "Paid", tone: "paid" },
  contracted: { label: "Pending · invoiced", tone: "contracted" },
  scheduled: { label: "Pending · to invoice", tone: "scheduled" },
  late: { label: "Pending · late", tone: "late" },
  pipeline: { label: "Pipeline", tone: "pipeline" },
};
const PROJECT_STATUS: Record<string, { label: string; tone: string }> = {
  paid: { label: "Paid", tone: "paid" },
  "part-paid": { label: "Part paid", tone: "part" },
  invoiced: { label: "Invoiced, unpaid", tone: "contracted" },
  "not-invoiced": { label: "Not invoiced", tone: "scheduled" },
  late: { label: "Late", tone: "late" },
};
const PROJ_COLS = [
  { k: "project", label: "Project", dir: "asc" },
  { k: "company", label: "Company", dir: "asc" },
  { k: "client", label: "Client", dir: "asc" },
  { k: "year", label: "Year", dir: "desc", num: true },
  { k: "sale", label: "Total", dir: "desc", num: true },
  { k: "paid", label: "Paid", dir: "desc", num: true },
  { k: "outstanding", label: "Pending", dir: "desc", num: true },
  { k: "status", label: "Status", dir: "asc" },
] as const;
type PKey = (typeof PROJ_COLS)[number]["k"];
type PFilter = "pending" | "part" | "paid" | "all";

export function MoneyInTab({ money, inflows, loading, failed, isPhone }: { money: Money | null; inflows: Inflows | undefined; loading: boolean; failed: boolean; isPhone: boolean }) {
  const [pickMonth, setPickMonth] = useState<string | null>(null);
  const [projFilter, setProjFilter] = useState<PFilter>("pending");
  const [psort, setPsort] = useState<{ key: PKey; dir: "asc" | "desc" }>({ key: "outstanding", dir: "desc" });
  const at = inflows?.generatedAt ? ` · ${new Date(inflows.generatedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "";

  return (
    <Card title={<>Money in — paid and pending <span className="muted ml-1 text-xs font-normal">from the CRM Finance module · excl. VAT{at}</span></>} icon={<TrendingUp size={16} />}>
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1">
        {TIERS.map((t) => (
          <span key={t.k} className="muted inline-flex items-center gap-1.5 text-xs">
            <i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: SEG_BG[t.k] }} /> {t.label} — {t.what}
          </span>
        ))}
      </div>
      {failed && <p className="muted text-sm">The CRM figures are not available right now.</p>}
      {loading && <p className="muted text-sm"><Loader2 size={13} className="mr-1 inline animate-spin" /> Reading the CRM…</p>}
      {money && <MoneyBody money={money} isPhone={isPhone} pickMonth={pickMonth} setPickMonth={setPickMonth} projFilter={projFilter} setProjFilter={setProjFilter} psort={psort} setPsort={setPsort} />}
    </Card>
  );
}

function MoneyBody({ money, isPhone, pickMonth, setPickMonth, projFilter, setProjFilter, psort, setPsort }: {
  money: Money; isPhone: boolean; pickMonth: string | null; setPickMonth: (m: string) => void;
  projFilter: PFilter; setProjFilter: (f: PFilter) => void; psort: { key: PKey; dir: "asc" | "desc" }; setPsort: (fn: (s: { key: PKey; dir: "asc" | "desc" }) => { key: PKey; dir: "asc" | "desc" }) => void;
}) {
  const back = isPhone ? 3 : 6, ahead = isPhone ? 9 : 12;
  const shown = money.all.slice(Math.max(0, money.cur - back), money.cur + ahead);
  const max = Math.max(...shown.map(monthTotal), 1);
  const nowYM = money.months[0]?.month;
  const sel = money.all.find((m) => m.month === pickMonth) || money.months[0] || shown[0];
  const y = money.yearRow;
  const counts: Record<PFilter, number> = {
    all: money.projects.length,
    pending: money.projects.filter((x) => x.status !== "paid").length,
    part: money.projects.filter((x) => x.status === "part-paid").length,
    paid: money.projects.filter((x) => x.status === "paid").length,
  };
  const rows = money.projects
    .filter((x) => projFilter === "all" || (projFilter === "paid" ? x.status === "paid" : projFilter === "part" ? x.status === "part-paid" : x.status !== "paid"))
    .sort((a, b) => {
      const sign = psort.dir === "asc" ? 1 : -1;
      const av = a[psort.key as keyof ProjectRow], bv = b[psort.key as keyof ProjectRow];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv))) * sign;
    });
  const clickP = (c: (typeof PROJ_COLS)[number]) => setPsort((st) => (st.key === c.k ? { key: c.k, dir: st.dir === "asc" ? "desc" : "asc" } : { key: c.k, dir: c.dir }));
  const itemsOf = (k: Seg) => (sel ? sel.items.filter((i) => (k === "late" ? i.basis === "late" : i.tier === k && i.basis !== "late")) : []);

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label={`Paid in ${y?.year || "this year"}`} value={y ? eurShort(y.cashIn) : "—"} sub={y ? `of ${eurShort(y.sales)} sales · ${Math.round((y.cashIn / (y.sales || 1)) * 100)}% received` : ""} />
        <Kpi label="Pending — invoiced" value={y ? eurShort(y.invoicedUnpaid || 0) : "—"} sub={y && !y.invoicedUnpaid ? "no invoices recorded in the CRM yet" : "invoiced, not paid"} />
        <Kpi label="Pending — not invoiced" value={y ? eurShort(y.notYetInvoiced || 0) : "—"} sub={money.late ? `incl. ${eurShort(money.late)} past its date` : "signed, to be invoiced"} />
        <Kpi label="Coming in, next 12 months" value={eurShort(money.contracted12 + money.scheduled12 + money.pipeline12)} sub={`incl. pipeline ${eurShort(money.pipeline12)}`} />
      </div>

      <div className="flex h-44 items-end gap-1 sm:gap-1.5">
        {shown.map((m) => {
          const tot = monthTotal(m);
          const on = sel?.month === m.month;
          return (
            <button
              key={m.month}
              className={clsx("flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1 rounded-md px-0.5 pb-0.5 transition-colors", on ? "bg-brand-50 ring-1 ring-brand-200" : "hover:bg-[var(--panel-2)]")}
              title={`${fmtYMLong(m.month)}: paid ${eur(m.paid)} · pending ${eur(m.contracted + m.scheduled + m.late)} · pipeline ${eur(m.pipeline)}`}
              onClick={() => setPickMonth(m.month)}
            >
              <span className="muted truncate text-[10px] num">{tot ? eurShort(tot) : ""}</span>
              <div className="flex w-full max-w-[34px] flex-col-reverse overflow-hidden rounded-t" style={{ height: `${Math.max(1, (tot / max) * 100)}%` }}>
                {SEGS.map((k) => m[k] > 0 && <div key={k} style={{ flex: m[k], background: SEG_BG[k] }} />)}
              </div>
              <span className={clsx("text-[10.5px]", m.month === nowYM ? "font-bold text-brand-700" : "muted")}>{m.month === nowYM ? "Now" : fmtYM(m.month)}</span>
            </button>
          );
        })}
      </div>
      {!money.hasPaidFeed && <p className="muted mt-1.5 text-xs">Past months stay empty until the CRM sends receipts per month (requested 26 Sep). The yearly paid figure above is already live.</p>}

      {sel && (
        <div className="mt-4 rounded-xl border border-[var(--line)] bg-[var(--panel-2)] p-3">
          <p className="mb-1.5 text-sm font-semibold">
            {fmtYMLong(sel.month)} — paid {eur(sel.paid)} · pending {eur(sel.contracted + sel.scheduled + sel.late)}{sel.pipeline ? ` · pipeline ${eur(sel.pipeline)}` : ""}{" "}
            <span className="muted text-xs font-normal">· tap a month above</span>
          </p>
          {SEGS.map((k) =>
            itemsOf(k).slice(0, 40).map((i, n) => (
              <div key={`${k}${n}`} className="flex items-center gap-2 border-t border-[var(--line)] py-1.5 text-[0.8125rem] first:border-t-0">
                <span className="shrink-0"><Pill tone={ITEM_STATUS[k].tone}>{ITEM_STATUS[k].label}</Pill></span>
                <span className="min-w-0 flex-1 truncate">
                  {i.project || i.deal || (i.description ? `${i.description} (not allocated to a project)` : "Unallocated receipt")}{i.company ? ` · ${i.company}` : ""}{i.client ? ` · ${i.client}` : ""}
                  <span className="muted">
                    {" "}
                    {k === "paid"
                      ? `${i.date ? fmtDay(i.date) : ""}${i.invoice ? ` · inv. ${i.invoice}` : ""}${i.bank ? ` · ${i.bank}` : ""}`
                      : i.period ? `${i.period} ${i.year}`
                      : i.basis === "spread" ? `1/${i.spreadOver} of ${i.year}`
                      : i.invoice ? `inv. ${i.invoice}${i.overdue ? " · overdue" : ""}`
                      : i.stage ? `${i.stage} · ${Math.round((i.probability || 0) * 100)}% of ${eurShort(i.value || 0)}` : ""}
                  </span>
                </span>
                <span className="shrink-0 num">{eur(i.amount || 0)}</span>
              </div>
            )),
          )}
          {!sel.items.length && <p className="muted text-sm">Nothing paid or pending this month.</p>}
        </div>
      )}

      <div className="mb-2 mt-5 flex flex-wrap items-center gap-2">
        <strong className="mr-1 text-sm">By project</strong>
        <div className="tabs">
          {([["pending", "Pending"], ["part", "Part paid"], ["paid", "Paid"], ["all", "All"]] as const).map(([k, l]) => (
            <button key={k} className="tab" aria-selected={projFilter === k} onClick={() => setProjFilter(k)} disabled={money.derived && (k === "paid" || k === "part")}>
              {l} <span className="count">{counts[k]}</span>
            </button>
          ))}
        </div>
      </div>
      {money.derived && <p className="muted mb-2 text-xs">Showing what is still pending per project. Paid amounts per project appear here as soon as the CRM sends them (requested 26 Sep).</p>}
      <div className="-mx-5 overflow-x-auto">
        <table className="table min-w-[760px]">
          <thead><tr>{PROJ_COLS.map((c) => <SortTh key={c.k} label={c.label} on={psort.key === c.k} dir={psort.dir} num={"num" in c} onClick={() => clickP(c)} />)}</tr></thead>
          <tbody>
            {rows.slice(0, 200).map((x, n) => {
              const st = PROJECT_STATUS[x.status] || PROJECT_STATUS["not-invoiced"]!;
              return (
                <tr key={`${x.project}|${x.year}|${x.period || ""}|${n}`}>
                  <td>{x.project}{x.period ? <span className="muted"> · {x.period}</span> : null}</td>
                  <td>{x.company || <span className="muted">—</span>}</td>
                  <td>{x.client || <span className="muted">—</span>}</td>
                  <td className="text-right num">{x.year}</td>
                  <td className="text-right num">{x.sale == null ? <span className="muted">—</span> : eur(x.sale)}</td>
                  <td className="text-right num">{x.paid == null ? <span className="muted">—</span> : eur(x.paid)}</td>
                  <td className="text-right font-semibold num">{eur(x.outstanding || 0)}</td>
                  <td>
                    <Pill tone={x.overdue ? "overdue" : st.tone}>{x.overdue ? "Invoiced · overdue" : st.label}</Pill>
                    {(x.unrecordedReceipt ?? 0) > 0 && <span className="muted text-[11px]" title="Marked paid in the CRM but the receipt is not recorded in the ledger yet"> receipt not recorded ({eurShort(x.unrecordedReceipt!)})</span>}
                    {x.lastPaidOn && <span className="muted text-[11px]"> {fmtDay(x.lastPaidOn)}</span>}
                  </td>
                </tr>
              );
            })}
            {!rows.length && <tr><td colSpan={8} className="muted !py-6 text-center">Nothing in this view.</td></tr>}
            {rows.length > 0 && (
              <tr className="bg-[var(--panel-2)] font-semibold">
                <td colSpan={4}>{rows.length} project{rows.length === 1 ? "" : "s"}</td>
                <td className="text-right num">{money.derived ? "" : eur(rows.reduce((a, x) => a + (x.sale || 0), 0))}</td>
                <td className="text-right num">{money.derived ? "" : eur(rows.reduce((a, x) => a + (x.paid || 0), 0))}</td>
                <td className="text-right num">{eur(rows.reduce((a, x) => a + (x.outstanding || 0), 0))}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {money.years.length > 0 && (
        <div className="-mx-5 mt-5 overflow-x-auto">
          <table className="table min-w-[640px]">
            <thead><tr><th>Year</th><th className="!text-right">Sales</th><th className="!text-right">Paid</th><th className="!text-right">Pending — invoiced</th><th className="!text-right">Pending — not invoiced</th><th className="!text-right">Profit</th></tr></thead>
            <tbody>
              {money.years.map((yy) => (
                <tr key={yy.year}>
                  <td>{yy.year}</td>
                  <td className="text-right num">{eur(yy.sales)}</td>
                  <td className="text-right font-semibold num">{eur(yy.cashIn)}</td>
                  <td className="text-right num">{eur(yy.invoicedUnpaid || 0)}</td>
                  <td className="text-right num">{eur(yy.notYetInvoiced || 0)}</td>
                  <td className="text-right num">{eur(yy.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
